import { useState, useEffect, useCallback } from "react";

interface Category {
  id: string;
  name: string;
  count: number;
}

interface Question {
  id: string;
  image: string;
  category: string;
  correct: string;
  options: string[];
}

interface QuizData {
  categories: Category[];
  questions: Question[];
}

type View = "categories" | "quiz" | "results";

const QUESTIONS_PER_SESSION = 20;
const STORAGE_KEY = "traffic-signs-quiz-progress";

interface SavedProgress {
  categoryId: string | null;
  questionIds: string[];
  answers: Record<number, string>;
  maxReached: number;
  currentIndex: number;
}

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = a[i] as T;
    a[i] = a[j] as T;
    a[j] = tmp;
  }
  return a;
}

function loadProgress(): SavedProgress | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveProgress(progress: SavedProgress) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
}

function clearProgress() {
  localStorage.removeItem(STORAGE_KEY);
}

export default function TrafficSignsQuiz() {
  const [data, setData] = useState<QuizData | null>(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<View>("categories");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [sessionQuestions, setSessionQuestions] = useState<Question[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [maxReached, setMaxReached] = useState(0);
  const [hasSavedProgress, setHasSavedProgress] = useState(false);

  useEffect(() => {
    fetch("/api/v1/traffic-signs/quiz")
      .then((r) => {
        if (!r.ok) throw new Error("API unavailable");
        return r.json();
      })
      .catch(() => fetch("/traffic-signs-quiz.json").then((r) => r.json()))
      .then((d: QuizData) => {
        setData(d);
        setLoading(false);
        setHasSavedProgress(!!loadProgress());
      })
      .catch(() => setLoading(false));
  }, []);

  const startQuiz = useCallback(
    (categoryId: string | null, restore?: SavedProgress) => {
      if (!data) return;

      let selected: Question[];
      if (restore) {
        const idSet = new Set(restore.questionIds);
        selected = data.questions.filter((q) => idSet.has(q.id));
        // Maintain original order
        const orderMap = Object.fromEntries(restore.questionIds.map((id, i) => [id, i]));
        selected.sort((a, b) => (orderMap[a.id] ?? 0) - (orderMap[b.id] ?? 0));
      } else {
        const pool =
          categoryId === null
            ? data.questions
            : data.questions.filter((q) => {
                const cat = data.categories.find((c) => c.id === categoryId);
                return cat && q.category === cat.name;
              });
        selected = shuffle(pool).slice(0, QUESTIONS_PER_SESSION);
      }

      setSessionQuestions(selected);
      setSelectedCategory(categoryId);
      setCurrentIndex(restore?.currentIndex ?? 0);
      setAnswers(restore?.answers ?? {});
      setMaxReached(restore?.maxReached ?? 0);
      setView("quiz");
    },
    [data]
  );

  const resumeQuiz = useCallback(() => {
    const progress = loadProgress();
    if (!progress || !data) return;
    startQuiz(progress.categoryId, progress);
  }, [data, startQuiz]);

  const handleAnswer = (option: string) => {
    if (answers[currentIndex] !== undefined) return;
    const newAnswers = { ...answers, [currentIndex]: option };
    setAnswers(newAnswers);
    const newMax = Math.max(maxReached, currentIndex + 1);
    setMaxReached(newMax);

    // Persist progress
    saveProgress({
      categoryId: selectedCategory,
      questionIds: sessionQuestions.map((q) => q.id),
      answers: newAnswers,
      maxReached: newMax,
      currentIndex,
    });
  };

  const handleNext = () => {
    if (currentIndex + 1 >= sessionQuestions.length) {
      clearProgress();
      setHasSavedProgress(false);
      setView("results");
    } else {
      const next = currentIndex + 1;
      setCurrentIndex(next);
      // Update persisted currentIndex
      saveProgress({
        categoryId: selectedCategory,
        questionIds: sessionQuestions.map((q) => q.id),
        answers,
        maxReached,
        currentIndex: next,
      });
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      const prev = currentIndex - 1;
      setCurrentIndex(prev);
      saveProgress({
        categoryId: selectedCategory,
        questionIds: sessionQuestions.map((q) => q.id),
        answers,
        maxReached,
        currentIndex: prev,
      });
    }
  };

  const handleFinish = () => {
    clearProgress();
    setHasSavedProgress(false);
    setView("results");
  };

  const handleRestartCategory = (categoryId: string | null) => {
    clearProgress();
    setHasSavedProgress(false);
    startQuiz(categoryId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin h-10 w-10 border-4 border-primary-600 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-600">Loading traffic signs...</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  // ── CATEGORIES VIEW ──────────────────────────────────────────────────────────
  if (view === "categories") {
    return (
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Traffic Signs Quiz</h1>
          <p className="mt-2 text-gray-600">
            Test your knowledge of UK traffic signs. Pick a category or try all signs.
          </p>
        </div>

        {/* Resume card */}
        {hasSavedProgress && (
          <div className="mb-6 rounded-xl border-2 border-amber-400 bg-amber-50 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-amber-900">You have a quiz in progress</p>
                <p className="text-sm text-amber-700 mt-1">Continue where you left off</p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => { clearProgress(); setHasSavedProgress(false); }}
                  className="rounded-lg border border-amber-400 px-3 py-1.5 text-sm text-amber-700 hover:bg-amber-100 transition-colors"
                >
                  Discard
                </button>
                <button
                  onClick={resumeQuiz}
                  className="rounded-lg bg-amber-500 px-4 py-1.5 text-sm font-semibold text-white hover:bg-amber-600 transition-colors"
                >
                  Resume
                </button>
              </div>
            </div>
          </div>
        )}

        {/* All Signs card */}
        <button
          onClick={() => startQuiz(null)}
          className="w-full mb-6 rounded-xl border-2 border-primary-600 bg-primary-600 p-5 text-left text-white hover:bg-primary-700 transition-colors"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-lg font-bold">All Traffic Signs</p>
              <p className="text-primary-100 text-sm mt-1">
                {data.questions.length} signs · {QUESTIONS_PER_SESSION} random questions per session
              </p>
            </div>
            <svg className="h-8 w-8 text-primary-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </div>
        </button>

        {/* Category grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => startQuiz(cat.id)}
              className="rounded-xl border border-gray-200 bg-white p-5 text-left hover:border-primary-400 hover:shadow-md transition-all"
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-semibold text-gray-900">{cat.name}</p>
                  <p className="text-sm text-gray-500 mt-1">{cat.count} signs</p>
                </div>
                <span className="rounded-full bg-primary-100 text-primary-700 text-xs font-semibold px-2.5 py-1">
                  Quiz
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ── QUIZ VIEW ─────────────────────────────────────────────────────────────────
  if (view === "quiz") {
    const q = sessionQuestions[currentIndex];
    if (!q) return null;
    const total = sessionQuestions.length;
    const answeredCount = Object.keys(answers).length;
    const progress = (answeredCount / total) * 100;
    const catLabel =
      selectedCategory === null
        ? "All Signs"
        : (data.categories.find((c) => c.id === selectedCategory)?.name ?? "");

    const selectedAnswer = answers[currentIndex] ?? null;
    const answered = selectedAnswer !== null;

    // Next is enabled when current is answered OR we're navigating among already-answered questions
    const canGoNext = currentIndex < total - 1 && currentIndex < maxReached;
    const canGoPrev = currentIndex > 0;
    const allAnswered = answeredCount >= total;

    return (
      <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => setView("categories")}
            className="flex items-center text-sm text-gray-500 hover:text-gray-800 transition-colors"
          >
            <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Categories
          </button>
          <span className="text-sm font-medium text-gray-600">
            {currentIndex + 1} / {total}
            {answeredCount > 0 && (
              <span className="ml-2 text-xs text-gray-400">({answeredCount} answered)</span>
            )}
          </span>
        </div>

        {/* Progress bar */}
        <div className="w-full bg-gray-200 rounded-full h-2 mb-6">
          <div
            className="bg-primary-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Category badge */}
        <p className="text-xs font-semibold uppercase tracking-wide text-primary-600 mb-4">{catLabel}</p>

        {/* Sign image */}
        <div className="rounded-xl border border-gray-200 bg-gray-50 overflow-hidden mb-6 flex items-center justify-center min-h-56">
          <img
            key={q.id}
            src={`/static/traffic-signs/${q.image}`}
            alt="Traffic sign"
            className="max-h-56 max-w-full object-contain p-4"
          />
        </div>

        <p className="text-lg font-semibold text-gray-900 mb-4">What does this sign mean?</p>

        {/* Answer options */}
        <div className="space-y-3 mb-6">
          {q.options.map((opt, idx) => {
            const label = ["A", "B", "C", "D"][idx];
            const isSelected = selectedAnswer === opt;
            const isCorrect = opt === q.correct;

            let borderColor = "border-gray-200 hover:border-primary-400 hover:bg-primary-50";
            let labelBg = "bg-gray-100 text-gray-700";

            if (answered && isCorrect) {
              borderColor = "border-green-500 bg-green-50";
              labelBg = "bg-green-500 text-white";
            } else if (answered && !isCorrect) {
              borderColor = isSelected
                ? "border-red-500 bg-red-50"
                : "border-red-200 bg-red-50/50";
              labelBg = "bg-red-400 text-white";
            }

            return (
              <button
                key={opt}
                onClick={() => handleAnswer(opt)}
                disabled={answered}
                className={`flex w-full items-center gap-3 rounded-xl border-2 p-4 text-left transition-all ${borderColor} ${answered ? "cursor-default" : "cursor-pointer"}`}
              >
                <span
                  className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold transition-colors ${labelBg}`}
                >
                  {label}
                </span>
                <span className="text-sm text-gray-800 flex-1">{opt}</span>
                {answered && isCorrect && (
                  <svg className="h-6 w-6 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                )}
                {answered && isSelected && !isCorrect && (
                  <svg className="h-6 w-6 text-red-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>

        {/* Navigation buttons */}
        <div className="flex items-center gap-3">
          <button
            onClick={handlePrev}
            disabled={!canGoPrev}
            className="flex items-center gap-2 rounded-xl border border-gray-300 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Previous
          </button>

          <div className="flex-1" />

          {allAnswered ? (
            <button
              onClick={handleFinish}
              className="rounded-xl bg-primary-600 px-5 py-3 text-sm font-semibold text-white hover:bg-primary-700 transition-colors"
            >
              See Results
            </button>
          ) : (
            <button
              onClick={handleNext}
              disabled={!canGoNext}
              className="flex items-center gap-2 rounded-xl bg-primary-600 px-4 py-3 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Next
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── RESULTS VIEW ──────────────────────────────────────────────────────────────
  const correctCount = sessionQuestions.filter((q, i) => answers[i] === q.correct).length;
  const pct = Math.round((correctCount / sessionQuestions.length) * 100);
  const passed = pct >= 70;
  const catLabel =
    selectedCategory === null
      ? "All Signs"
      : (data.categories.find((c) => c.id === selectedCategory)?.name ?? "");

  return (
    <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8 py-8">
      {/* Score card */}
      <div className={`rounded-2xl p-8 text-center mb-8 ${passed ? "bg-green-50 border border-green-200" : "bg-orange-50 border border-orange-200"}`}>
        <div className={`text-5xl font-bold mb-2 ${passed ? "text-green-600" : "text-orange-500"}`}>
          {pct}%
        </div>
        <p className="text-xl font-semibold text-gray-900 mb-1">
          {correctCount} / {sessionQuestions.length} correct
        </p>
        <p className={`text-sm font-medium ${passed ? "text-green-600" : "text-orange-500"}`}>
          {passed ? "Well done!" : "Keep practising!"}
        </p>
        <p className="text-xs text-gray-500 mt-1">{catLabel}</p>
      </div>

      {/* Action buttons */}
      <div className="flex gap-3 mb-8">
        <button
          onClick={() => handleRestartCategory(selectedCategory)}
          className="flex-1 rounded-xl bg-primary-600 py-3 text-white font-semibold hover:bg-primary-700 transition-colors"
        >
          Try Again
        </button>
        <button
          onClick={() => setView("categories")}
          className="flex-1 rounded-xl border border-gray-300 py-3 text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
        >
          Change Category
        </button>
      </div>

      {/* Review answers */}
      <h2 className="text-lg font-bold text-gray-900 mb-4">Review</h2>
      <div className="space-y-4">
        {sessionQuestions.map((question, idx) => {
          const selected = answers[idx];
          const correct = selected === question.correct;
          return (
            <div
              key={question.id}
              className={`rounded-xl border p-4 ${correct ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}`}
            >
              <div className="flex gap-4">
                <img
                  src={`/static/traffic-signs/${question.image}`}
                  alt="Traffic sign"
                  className="h-16 w-16 object-contain flex-shrink-0 rounded-lg bg-white border border-gray-100"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {correct ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-100 rounded-full px-2 py-0.5">
                        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                        Correct
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-700 bg-red-100 rounded-full px-2 py-0.5">
                        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        Incorrect
                      </span>
                    )}
                  </div>
                  {!correct && selected && (
                    <p className="text-xs text-red-700 mb-1">
                      Your answer: <span className="font-medium">{selected}</span>
                    </p>
                  )}
                  <p className="text-xs text-gray-800">
                    <span className="font-semibold">Correct: </span>
                    {question.correct}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
